import React from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import SucceedoProfile from '../components/Succeedo/Profile/SucceedoProfile';
import AppLayout from '../components/Succeedo/Layout/AppLayout';

// Named distinctly from the Succession profile page's GetCurrentUserProfile.
// Apollo's refetchQueries matches by OPERATION NAME against active queries, so a
// shared name lets a refetch resolve to whichever document happens to be mounted
// — and a document that omits a field will overwrite the normalized User entity
// with a stale value for it.
const GET_SUCCEEDO_PROFILE = gql`
  query GetSucceedoProfile {
    currentUser {
      id
      name
      email
      succeedo_persona
      succeedo_goal
      time_horizon
      role_title
      succeedo_company_name
      province
      succeedo_country
      entity_type
      industry_sector
      industry_description
      owner_role
      business_size_band
      sale_objective
      desired_timeline
      business_name_confidential
      buyer_profile
      linkedin_url
      profile_picture
      created_at
    }
  }
`;

const UPLOAD_SUCCEEDO_AVATAR = gql`
  mutation UploadSucceedoAvatar($file: File!) {
    upload_succeedo_avatar(file: $file) {
      avatar_url
      errors
    }
  }
`;

const SAVE_SUCCEEDO_ONBOARDING = gql`
  mutation SaveSucceedoOnboarding(
    $persona: String!
    $goal: String!
    $timeHorizon: String
    $roleTitle: String
    $companyName: String
    $geography: String
    $ownerRole: String
    $industrySector: String
    $industryDescription: String
    $businessSizeBand: String
    $saleObjective: String
    $desiredTimeline: String
    $businessNameConfidential: Boolean
    $buyerType: String
    $acquisitionIntent: String
    $preferredIndustries: [String!]
    $targetGeography: String
    $targetSizeRange: String
    $dealRole: String
    $budgetRange: String
    $linkedinUrl: String
  ) {
    save_succeedo_onboarding(
      persona: $persona
      goal: $goal
      time_horizon: $timeHorizon
      role_title: $roleTitle
      company_name: $companyName
      geography: $geography
      owner_role: $ownerRole
      industry_sector: $industrySector
      industry_description: $industryDescription
      business_size_band: $businessSizeBand
      sale_objective: $saleObjective
      desired_timeline: $desiredTimeline
      business_name_confidential: $businessNameConfidential
      buyer_type: $buyerType
      acquisition_intent: $acquisitionIntent
      preferred_industries: $preferredIndustries
      target_geography: $targetGeography
      target_size_range: $targetSizeRange
      deal_role: $dealRole
      budget_range: $budgetRange
      linkedin_url: $linkedinUrl
    ) {
      user {
        id
        succeedo_persona
        succeedo_goal
        linkedin_url
        profile_picture
      }
      errors
    }
  }
`;

const SAVE_ENTITY_TYPE = gql`
  mutation SaveEntityType($entityType: String) {
    save_entity_type(entity_type: $entityType) {
      entity_type
      errors
    }
  }
`;

function SucceedoProfileWithData() {
  const { data, loading: queryLoading, refetch } = useQuery(GET_SUCCEEDO_PROFILE);
  const [saveOnboarding, { loading: mutationLoading }] = useMutation(SAVE_SUCCEEDO_ONBOARDING);
  const [uploadAvatar] = useMutation(UPLOAD_SUCCEEDO_AVATAR);
  const [saveEntityType] = useMutation(SAVE_ENTITY_TYPE);

  const currentUser = data?.currentUser || null;

  const handleSave = async (variables) => {
    const { data: result } = await saveOnboarding({ variables });
    if (result?.save_succeedo_onboarding?.errors?.length > 0) {
      throw new Error(result.save_succeedo_onboarding.errors.join(', '));
    }
    return result;
  };

  const handleSaveEntityType = async (entityType) => {
    const { data: result } = await saveEntityType({ variables: { entityType } });
    const payload = result?.save_entity_type;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    await refetch();
    return payload;
  };

  const handleUploadAvatar = async (file) => {
    const { data: result } = await uploadAvatar({
      variables: { file },
      context: { hasUpload: true },
    });
    const payload = result?.upload_succeedo_avatar;
    if (payload?.errors?.length > 0) {
      throw new Error(payload.errors.join(', '));
    }
    // Refetch user data so profile_picture reflects the new avatar
    await refetch();
    return payload;
  };

  if (queryLoading) {
    return (
      <AppLayout breadcrumb="PROFILE">
        <div className="flex items-center justify-center p-12">
          <div className="text-muted-foreground text-[16px]">Loading profile...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb="PROFILE">
      <SucceedoProfile
        currentUser={currentUser}
        onSave={handleSave}
        onUploadAvatar={handleUploadAvatar}
        onSaveEntityType={handleSaveEntityType}
        loading={mutationLoading}
      />
    </AppLayout>
  );
}

const SucceedoProfileContainer = (_props, _railsContext) => (
  <ApolloProvider client={client}>
    <SucceedoProfileWithData />
  </ApolloProvider>
);

export default SucceedoProfileContainer;
